/**
 * usePreloadTileIcons - Preloads tile icon images before the home screen reveals.
 *
 * After the app catalog and layout have settled, this hook reads all visible
 * tile IDs from the homescreen store and preloads their icon images from R2
 * (/api/media/{id}.png). This ensures icons are in the browser cache when
 * the skeleton fades out, preventing the "tiles appear without icons" flash.
 *
 * Returns `true` once all icons have loaded (or timed out).
 */

import { useState, useEffect, useRef } from 'react';
import { useHomeScreenStore, useAppRegistryStore } from '@room/shared-ui';

const MEDIA_BASE = '/api/media';
/** Maximum time to wait for icon preloading before force-revealing */
const PRELOAD_TIMEOUT_MS = 3000;
/** Minimum number of icons that must load before we consider it ready (ratio) */
const MIN_LOADED_RATIO = 0.6;

/**
 * Preload a single image URL. Resolves with true on success, false on error.
 */
function preloadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/**
 * Check if an icon value is an image URL (already loadable without MediaIcon).
 */
function isImageUrl(icon: string): boolean {
  return (
    icon.startsWith('http://') ||
    icon.startsWith('https://') ||
    icon.startsWith('data:image/') ||
    icon.startsWith('/api/')
  );
}

export function usePreloadTileIcons(dataReady: boolean): boolean {
  const [iconsReady, setIconsReady] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!dataReady || startedRef.current) return;
    startedRef.current = true;

    const store = useHomeScreenStore.getState();
    const sections = store.getSections();

    // Collect all visible tile IDs that will use MediaIcon
    const tileIds: string[] = [];
    const imageUrls: string[] = [];

    for (const section of sections) {
      for (const tile of section.items) {
        // Skip tiles with visibility checks that return false
        if (tile.visible && !tile.visible()) continue;

        if (isImageUrl(tile.icon)) {
          // Icon is already an image URL — preload it directly
          imageUrls.push(tile.icon);
        } else {
          // Icon is an emoji — MediaIcon will load from /api/media/{id}.png
          tileIds.push(tile.id);
        }
      }
    }

    // Also preload suggestion icons (uninstalled dynamic apps shown in the row)
    const registryState = useAppRegistryStore.getState();
    const installedApps = new Set(registryState.installedApps);
    const dynamicSuggestions = Object.values(registryState.manifests)
      .filter((m) => m.category === 'dynamic' && m.tile && !installedApps.has(m.id))
      .slice(0, 6); // AppSuggestions shows max 6

    for (const manifest of dynamicSuggestions) {
      if (manifest.mediaIconPrompt) {
        tileIds.push(manifest.id);
      }
    }

    // Build all preload URLs (deduplicate)
    const urlSet = new Set([
      ...tileIds.map((id) => `${MEDIA_BASE}/${id}.png`),
      ...imageUrls,
    ]);
    const allUrls = Array.from(urlSet);

    if (allUrls.length === 0) {
      setIconsReady(true);
      return;
    }

    // Race: preload all icons vs timeout
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      setIconsReady(true);
    };

    // Timeout fallback
    const timer = setTimeout(settle, PRELOAD_TIMEOUT_MS);

    // Preload all icons in parallel, settle when enough have loaded
    const results: boolean[] = [];
    let completed = 0;

    allUrls.forEach((url) => {
      preloadImage(url).then((ok) => {
        if (settled) return;
        completed++;
        results.push(ok);

        // Check if enough icons loaded to reveal
        const loadedCount = results.filter(Boolean).length;
        const ratio = loadedCount / allUrls.length;

        if (completed === allUrls.length || ratio >= MIN_LOADED_RATIO) {
          clearTimeout(timer);
          settle();
        }
      });
    });

    return () => {
      clearTimeout(timer);
    };
  }, [dataReady]);

  return iconsReady;
}
